import { z } from 'zod';

import { ELIGIBILITY_PROVIDER_NAMES } from './schema';

// Provider-qualified external client ref: `<source>:<ref>`. Internaltools is
// the company source of truth (there is no local clients table to key on), so
// member-side rows carry the provider's own ref, namespaced by the provider
// that resolved it — two providers can use the same raw ref without
// colliding. `legacy` marks rows backfilled from the retired clients table;
// it parses but is never composed by a provider path.
export const CLIENT_REF_SOURCES = [...ELIGIBILITY_PROVIDER_NAMES, 'legacy'] as const;
export const clientRefSourceSchema = z.enum(CLIENT_REF_SOURCES);
export type ClientRefSource = z.infer<typeof clientRefSourceSchema>;

export const qualifiedClientRefSchema = z.string().refine(
  (value) => {
    const parsed = parseClientRef(value);
    return parsed !== null;
  },
  { message: 'expected "<source>:<ref>" with a known source and non-empty ref' },
);
export type QualifiedClientRef = z.infer<typeof qualifiedClientRefSchema>;

export function composeClientRef(source: ClientRefSource, ref: string): string {
  if (ref.length === 0) {
    throw new Error('client ref must be non-empty');
  }
  return `${source}:${ref}`;
}

export function parseClientRef(value: string): { source: ClientRefSource; ref: string } | null {
  const separator = value.indexOf(':');
  if (separator <= 0 || separator === value.length - 1) return null;
  const source = clientRefSourceSchema.safeParse(value.slice(0, separator));
  if (!source.success) return null;
  return { source: source.data, ref: value.slice(separator + 1) };
}
